// =============================================================================
// SIREPPAR - Corte de hojas del editor de la resolución (como en Word)
// =============================================================================
// El editor de la resolución del Secretario es UN contenteditable por documento,
// dibujado sobre fondos de hoja oficio (con su membrete) separados por un hueco.
// Para que se vea dónde corta cada hoja —y que corte igual que el PDF— se aplica
// la misma regla que el renderer del backend (utils/htmlResolucionPdf.ts):
//
//   "mantener el bloque junto": un bloque (párrafo, ítem de lista o la firma) que
//   no entra en lo que queda del cuerpo de la hoja pasa entero a la siguiente. El
//   espacio de 10 pt de abajo del párrafo no cuenta. Un bloque más alto que una
//   hoja entera se deja desbordar (el PDF sí lo parte por renglones).
//
//   "la firma nunca queda sola": si la firma no entra en la hoja del último
//   bloque, ese bloque se va con ella a la hoja siguiente (en el editor, entero;
//   el PDF, si no puede moverlo entero, se lleva solo sus últimos renglones).
//
// El empuje se aplica con un <style> generado (`:nth-child()` + `padding-top`), y
// NO tocando el contenido: un estilo en línea terminaría en el innerHTML que se
// manda al backend, y cambiar el DOM editable movería el cursor. Es padding y no
// margin porque el margin colapsaría con el del párrafo anterior.
// =============================================================================

/** Geometría de la hoja en puntos, tal como la manda el backend (`geometriaHoja`). */
export interface GeometriaHoja {
  anchoPt: number;
  altoPt: number;
  margenIzqPt: number;
  margenDerPt: number;
  membreteSupPt: number;
  altoLeyendaPt: number;
  separacionLeyendaLogoPt: number;
  anchoLogoPt: number;
  altoLogoPt: number;
  cuerpoTopPt: number;
  limiteInfPt: number;
}

/** 1 pt = 96/72 px (CSS). */
export const PX_POR_PT = 96 / 72;

/** Hueco gris entre una hoja y la siguiente, en px. */
export const HUECO_ENTRE_HOJAS_PX = 16;

export function ptAPx(pt: number): number {
  return pt * PX_POR_PT;
}

interface BloqueMedido {
  selector: string;
  /** El bloque de firma (siempre el último). */
  esFirma?: boolean;
  /** Borde superior del bloque, relativo al contenedor de las hojas (px). */
  top: number;
  /** Alto del bloque sin márgenes (px). */
  alto: number;
}

/** Tolerancia de redondeo: offsetTop/offsetHeight vienen en px enteros. */
const TOL = 1;

/**
 * Simula el corte con las posiciones naturales (sin empujes) y devuelve cuánto
 * hay que empujar cada bloque y cuántas hojas hacen falta.
 */
export function calcularCortes(
  bloques: BloqueMedido[],
  geo: GeometriaHoja,
): { empujes: Array<{ selector: string; px: number }>; hojas: number } {
  const alto = ptAPx(geo.altoPt);
  const paso = alto + HUECO_ENTRE_HOJAS_PX;
  const cuerpoTop = (h: number) => h * paso + ptAPx(geo.cuerpoTopPt);
  const limite = (h: number) => h * paso + ptAPx(geo.limiteInfPt);

  const empujes: Array<{ selector: string; px: number }> = [];
  const empujar = (selector: string, px: number) => {
    const previo = empujes.find((e) => e.selector === selector);
    if (previo) previo.px += px; else empujes.push({ selector, px });
  };
  // Dónde quedó cada bloque (ya empujado) y en qué hoja.
  const ubicados: Array<{ selector: string; top: number; alto: number; hoja: number }> = [];
  let desplaz = 0;
  let hoja = 0;
  for (const b of bloques) {
    let top = b.top + desplaz;

    // La firma no queda sola: si no entra en la hoja del último bloque, ese bloque
    // (si no está ya al comienzo de su hoja) pasa entero a la siguiente con ella.
    const ultimo = ubicados[ubicados.length - 1];
    if (b.esFirma && ultimo && top + b.alto > limite(ultimo.hoja) + TOL && ultimo.top > cuerpoTop(ultimo.hoja) + TOL) {
      const extra = cuerpoTop(ultimo.hoja + 1) - ultimo.top;
      empujar(ultimo.selector, extra);
      desplaz += extra;
      top += extra;
      hoja = ultimo.hoja + 1;
      ultimo.top += extra;
      ultimo.hoja = hoja;
      while (ultimo.top + ultimo.alto > limite(hoja) + TOL && hoja < 500) hoja++;
    }

    // Si el bloque arranca más allá del cuerpo de la hoja actual (solo pasa después
    // de un bloque que desbordó), sigue en la hoja que le toca.
    while (top >= limite(hoja) - TOL && hoja < 500) hoja++;
    let empuje = 0;
    if (top < cuerpoTop(hoja) - TOL) {
      empuje = cuerpoTop(hoja) - top; // quedó sobre el membrete o el hueco
    } else if (top + b.alto > limite(hoja) + TOL && top > cuerpoTop(hoja) + TOL) {
      hoja++;
      empuje = cuerpoTop(hoja) - top; // no entra: pasa entero a la hoja siguiente
    }
    if (empuje > TOL) {
      empujar(b.selector, empuje);
      desplaz += empuje;
      top += empuje;
    }
    ubicados.push({ selector: b.selector, top, alto: b.alto, hoja });
    // Bloque más alto que una hoja: desborda y lo que sigue va después.
    while (top + b.alto > limite(hoja) + TOL && hoja < 500) hoja++;
  }
  return { empujes, hojas: hoja + 1 };
}

/** Suma de offsetTop hasta el contenedor (que tiene que estar posicionado). */
function topRelativo(el: HTMLElement, contenedor: HTMLElement): number {
  let y = 0;
  let n: HTMLElement | null = el;
  while (n && n !== contenedor) {
    y += n.offsetTop;
    n = n.offsetParent as HTMLElement | null;
  }
  return y;
}

/**
 * Mide los bloques del cuerpo editable (los ítems de las listas cuentan de a uno)
 * y la firma, calcula los cortes y escribe las reglas en `estilo`.
 * Devuelve la cantidad de hojas.
 */
export function paginarDocumento(params: {
  contenedor: HTMLElement;
  cuerpo: HTMLElement;
  firma: HTMLElement | null;
  estilo: HTMLStyleElement;
  /** Selector que identifica al cuerpo, p. ej. `[data-cuerpo="resolucion"]`. */
  selectorCuerpo: string;
  selectorFirma: string;
  geo: GeometriaHoja;
}): number {
  const { contenedor, cuerpo, firma, estilo, selectorCuerpo, selectorFirma, geo } = params;
  // Primero se sacan los empujes anteriores, para medir las posiciones naturales.
  estilo.textContent = '';

  const bloques: BloqueMedido[] = [];
  const medir = (el: HTMLElement, selector: string, esFirma = false) =>
    bloques.push({ selector, esFirma, top: topRelativo(el, contenedor), alto: el.offsetHeight });
  Array.from(cuerpo.children).forEach((hijo, i) => {
    const selector = `${selectorCuerpo} > :nth-child(${i + 1})`;
    if (hijo.tagName === 'UL' || hijo.tagName === 'OL') {
      Array.from(hijo.children).forEach((li, j) => medir(li as HTMLElement, `${selector} > :nth-child(${j + 1})`));
    } else {
      medir(hijo as HTMLElement, selector);
    }
  });
  if (firma) medir(firma, selectorFirma, true);

  const { empujes, hojas } = calcularCortes(bloques, geo);
  estilo.textContent = empujes
    .map((e) => `${e.selector} { padding-top: ${e.px.toFixed(2)}px !important; }`)
    .join('\n');
  return hojas;
}
